/**
 * <AdminConfirmDialog> — type-CONFIRM gate wrapping <ConfirmModal>.
 *
 * signup-billing-mvp Phase 5 Session 6 (2026-05-13).
 *
 * Used by destructive admin operations:
 *   - <PricingConfigEditor> (ratio change — global blast radius)
 *   - <BillingOverrides> (reset_subscription / lock — per-user destructive)
 *
 * The dialog renders a `summary` slot (typically an OLD→NEW diff) plus an
 * <input> with placeholder "Type CONFIRM to submit". The submit button stays
 * disabled until the user types exactly `CONFIRM` (case-sensitive). On
 * confirm, the parent's `onConfirm()` callback fires + the dialog stays open
 * so the parent can show a final spinner/error state before closing.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { ConfirmModal } from '@/components/ui';

interface AdminConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  body: ReactNode;
  /** Optional summary block (e.g. OLD→NEW diff). Renders above the CONFIRM input. */
  summary?: ReactNode;
  /** Submit button label when armed. Defaults to "Submit". */
  confirmLabel?: string;
  /** Renders the submit button in the danger variant. */
  dangerColor?: boolean;
}

const CONFIRM_TOKEN = 'CONFIRM';

export function AdminConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  body,
  summary,
  confirmLabel = 'Submit',
  dangerColor = false,
}: AdminConfirmDialogProps) {
  const [typed, setTyped] = useState('');

  // Reset typed state every time the dialog opens.
  useEffect(() => {
    if (!open) setTyped('');
  }, [open]);

  const armed = typed === CONFIRM_TOKEN;

  return (
    <ConfirmModal
      open={open}
      onClose={onClose}
      onConfirm={() => {
        if (armed) onConfirm();
      }}
      title={title}
      confirmLabel={armed ? confirmLabel : `${confirmLabel} (locked)`}
      danger={dangerColor}
    >
      <div className="flex flex-col gap-3" data-testid="admin-confirm-dialog">
        <div className="text-sm font-body text-text leading-relaxed">{body}</div>
        {summary && (
          <div
            className="px-3 py-2 bg-input-bg border border-panel-border rounded-[2px] text-xs font-mono text-text-muted"
            data-testid="admin-confirm-summary"
          >
            {summary}
          </div>
        )}
        <label className="flex flex-col gap-1">
          <span className="text-xs font-mono text-text-muted tracking-[1.1px] uppercase">
            Type CONFIRM to submit
          </span>
          <input
            type="text"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder="Type CONFIRM to submit"
            className="px-3 py-2 bg-input-bg border border-input-border rounded-[2px] text-text-bright text-sm font-mono outline-none focus:border-accent/20"
            data-testid="admin-confirm-input"
            autoFocus
            spellCheck={false}
            autoComplete="off"
          />
        </label>
        <span
          className="text-micro font-mono text-text-dim uppercase tracking-[1.2px]"
          data-testid="admin-confirm-armed-state"
          data-armed={armed ? 'true' : 'false'}
        >
          {armed ? 'Ready to submit' : 'Submit disabled — type CONFIRM (case-sensitive)'}
        </span>
      </div>
    </ConfirmModal>
  );
}
